import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { cityFromAddress, parseDate } from '../../utils/adminDates';
import type { Case } from '../../types/admin';
const monthTitle = new Intl.DateTimeFormat('da-DK', {
  month: 'long',
  year: 'numeric'
});
const dayTitle = new Intl.DateTimeFormat('da-DK', {
  weekday: 'long',
  day: 'numeric',
  month: 'long'
});
const weekdays = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];
function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Enkel månedskalender over planlagte sager — ingen avanceret booking. */
export function Calendar() {
  const {
    cases
  } = useAdminData();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const byDay = useMemo(() => {
    const map = new Map<string, Case[]>();
    cases.forEach(item => {
      const date = parseDate(item.plannedDate);
      if (!date) return;
      const key = dateKey(date);
      map.set(key, [...(map.get(key) ?? []), item]);
    });
    return map;
  }, [cases]);
  const cells = useMemo(() => {
    const offset = (month.getDay() + 6) % 7;
    const start = new Date(month.getFullYear(), month.getMonth(), 1 - offset);
    return Array.from({
      length: 42
    }, (_, index) => {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
      return {
        date,
        key: dateKey(date),
        inMonth: date.getMonth() === month.getMonth()
      };
    });
  }, [month]);
  const todayKey = dateKey(new Date());
  const monthAgenda = cells.filter(cell => cell.inMonth && byDay.has(cell.key)).map(cell => ({
    ...cell,
    items: byDay.get(cell.key) ?? []
  }));
  function shift(delta: number) {
    setMonth(current => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  }
  return <div className="px-4 py-6 md:px-8 md:py-8" data-id="element-889" data-mp="3ihscpdpdptss">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-890" data-mp="3igha3h7o6s39">
        <div data-id="element-891" data-mp="3dj7jhy8joadu">
          <h1 className="font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-none tracking-tight text-ink" data-id="element-892" data-mp="3dhmq4bxyuxhk">
            Kalender
          </h1>
          <p className="mt-2 text-[15px] text-ink-soft" data-id="element-893" data-mp="33ki2s8sdwvz6">Dine planlagte opgaver, ud fra sagernes planlagte dato.</p>
        </div>
        <div className="flex items-center gap-2" data-id="element-894" data-mp="2zkhm0hhtc8oy">
          <button type="button" onClick={() => shift(-1)} aria-label="Forrige måned" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line text-ink-soft transition-colors duration-150 hover:border-accent/50 hover:text-ink" data-id="element-895" data-mp="2ulejb3pb2ek5">
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-896" data-mp="10bzn4gxp3dq9" />
          </button>
          <p className="min-w-[10rem] text-center font-display text-lg font-bold text-ink" aria-live="polite" data-id="element-897" data-mp="15bc8ex8x4cqq">
            {capitalize(monthTitle.format(month))}
          </p>
          <button type="button" onClick={() => shift(1)} aria-label="Næste måned" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line text-ink-soft transition-colors duration-150 hover:border-accent/50 hover:text-ink" data-id="element-898" data-mp="1f1xc2z3g87gi">
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-899" data-mp="rbt98ty34bqk" />
          </button>
        </div>
      </div>

      {/* Månedsgitter på desktop */}
      <div className="mt-6 hidden overflow-hidden rounded-card border border-line bg-white md:block" data-id="element-900" data-mp="vbtp8j0u0uzq">
        <div className="grid grid-cols-7 border-b border-line" data-id="element-901" data-mp="22tkm85chr3w3">
          {weekdays.map(day => <p key={day} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-902" data-mp="26v64y7bpice7">
              {day}
            </p>)}
        </div>
        <div className="grid grid-cols-7" data-id="element-903" data-mp="20ee43ui25rb4">
          {cells.map((cell, index) => {
          const items = byDay.get(cell.key) ?? [];
          const isToday = cell.key === todayKey;
          return <div key={cell.key} className={`min-h-[7.5rem] border-line p-2 ${index % 7 !== 6 ? 'border-r' : ''} ${index < 35 ? 'border-b' : ''} ${cell.inMonth ? '' : 'bg-canvas/60'}`} data-id="element-904" data-mp="2a9pmqnwl7io5" data-mpi={index}>
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm ${isToday ? 'bg-accent font-semibold text-white' : cell.inMonth ? 'text-ink-soft' : 'text-ink-faint'}`} data-id="element-905" data-mp="2xzjxmzxcix5r">
                  {cell.date.getDate()}
                </span>
                <ul className="mt-1 flex flex-col gap-1" data-id="element-906" data-mp="3llvw7gwuu421">
                  {items.slice(0, 2).map(item => <li key={item.id} data-id="element-907" data-mp="3cnaiq068pf84">
                      <Link to={`/admin/sager/${item.id}`} className="block truncate rounded-md border border-accent/30 bg-accent/10 px-2 py-1 text-xs text-ink transition-colors duration-150 hover:border-accent/60" data-id="element-908" data-mp="3cm8usv44ky8z">
                        {item.title}
                      </Link>
                    </li>)}
                  {items.length > 2 && <li className="px-2 text-xs text-ink-muted" data-id="element-909" data-mp="2tydnnu30qiou">+ {items.length - 2} flere</li>}
                </ul>
              </div>;
        })}
        </div>
      </div>

      {/* Dagsliste — altid på mobil, som opsummering på desktop */}
      <section className="mt-6 md:mt-8" data-id="element-910" data-mp="2oxg7to5he0qi">
        <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-911" data-mp="2owekolb6xnsf">Opgaver i måneden</h2>
        {monthAgenda.length === 0 ? <p className="mt-3 rounded-card border border-line bg-white px-5 py-8 text-center text-sm text-ink-muted" data-id="element-912" data-mp="ujc5vufobkyl">
            Ingen planlagte opgaver i {monthTitle.format(month)}.
          </p> : <ol className="mt-3 flex flex-col gap-3" data-id="element-913" data-mp="gq2ujo860pdf">
            {monthAgenda.map(day => <li key={day.key} className="rounded-card border border-line bg-white p-2" data-id="element-914" data-mp="1dgd857huf6yu">
                <p className={`px-3 pb-1 pt-2 text-sm font-semibold ${day.key === todayKey ? 'text-accent' : 'text-ink-soft'}`} data-id="element-915" data-mp="1de0uh6j4birl">
                  {capitalize(dayTitle.format(day.date))}
                  {day.key === todayKey && ' · I dag'}
                </p>
                <ul className="divide-y divide-line/70" data-id="element-916" data-mp="ziyefqkk4d12">
                  {day.items.map(item => <li key={item.id} data-id="element-917" data-mp="3k6t7yo62u4p5">
                      <Link to={`/admin/sager/${item.id}`} className="flex items-center gap-3 rounded-xl px-3 py-3 transition-colors duration-150 hover:bg-canvas-deep" data-id="element-918" data-mp="3k5ruye5i384s">
                        <span className="min-w-0 flex-1" data-id="element-919" data-mp="3ezv63zlh1t07">
                          <span className="block truncate text-[15px] font-semibold text-ink" data-id="element-920" data-mp="3k00zbk7e3nke">{item.title}</span>
                          <span className="block truncate text-sm text-ink-muted" data-id="element-921" data-mp="3oskaybmio8mk">
                            {[item.customerName, cityFromAddress(item.address)].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <StatusBadge status={item.status} data-id="element-922" data-mp="6mfibmyclhnj" />
                      </Link>
                    </li>)}
                </ul>
              </li>)}
          </ol>}
      </section>
    </div>;
}